import React, { useState } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const WeightConversion = () => {
  // State variables for Weight Conversion
  const [weight, setWeight] = useState(0); // Input weight to convert
  const [unitFrom, setUnitFrom] = useState("kg"); // Unit to convert from
  const [unitTo, setUnitTo] = useState("lbs"); // Unit to convert to
  const [convertedWeight, setConvertedWeight] = useState(0); // Converted weight

  // Conversion factors
  const conversionFactors = {
    "kg-lbs": 2.20462,
    "lbs-kg": 0.453592,
    "kg-oz": 35.274,
    "oz-kg": 0.0283495,
    "lbs-oz": 16,
    "oz-lbs": 0.0625,
  };

  // Event handlers to update state values
  const handleWeightChange = (e) => setWeight(parseFloat(e.target.value) || 0);
  const handleUnitFromChange = (e) => setUnitFrom(e.target.value);
  const handleUnitToChange = (e) => setUnitTo(e.target.value);

  // Function to perform the weight conversion
  const convertWeight = () => {
    const conversionKey = `${unitFrom}-${unitTo}`;
    const factor = conversionFactors[conversionKey];

    if (factor) {
      setConvertedWeight(weight * factor);
    } else {
      setConvertedWeight(0);
    }
  };

  // Trigger the conversion when the weight or units change
  React.useEffect(() => {
    convertWeight();

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [weight, unitFrom, unitTo]);

  return (
    <Layout title="Weight Conversion Calculator" path="weight-conversion">
      <section className="section-padding" id="weight-conversion">
        <div className="container mt-5">
          <div className="row">
            {/* Weight Conversion Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Weight Conversion Calculator
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Weight */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="weight" className="form-label">
                        Weight
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Weight:</strong> The numerical value of the weight you want to convert."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="weight"
                        placeholder="Enter weight"
                        value={weight || ""}
                        onChange={handleWeightChange}
                      />
                    </div>

                    {/* Select Unit From */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="unitFrom" className="form-label">
                        From Unit
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>From Unit:</strong> The unit of the weight you are starting with (e.g., kilograms)."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <select
                        className="form-select"
                        id="unitFrom"
                        value={unitFrom}
                        onChange={handleUnitFromChange}
                      >
                        <option value="kg">Kilograms (kg)</option>
                        <option value="lbs">Pounds (lbs)</option>
                        <option value="oz">Ounces (oz)</option>
                      </select>
                    </div>

                    {/* Select Unit To */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="unitTo" className="form-label">
                        To Unit
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>To Unit:</strong> The unit you want to convert the weight to (e.g., pounds)."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <select
                        className="form-select"
                        id="unitTo"
                        value={unitTo}
                        onChange={handleUnitToChange}
                      >
                        <option value="kg">Kilograms (kg)</option>
                        <option value="lbs">Pounds (lbs)</option>
                        <option value="oz">Ounces (oz)</option>
                      </select>
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Converted Weight:{" "}
                        <span className="fw-bold ms-3">
                          {convertedWeight === 0
                            ? "0"
                            : convertedWeight.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to Weight Conversion
                </h3>

                {/* What is Weight Conversion? */}
                <h4 className="mt-3 mb-3">What is Weight Conversion?</h4>
                <p>
                  **Weight Conversion** refers to the process of converting
                  units of weight between different measurement systems. It is
                  an essential tool for industries that deal with international
                  trade, food, and retail industries, where different units of
                  weight are used across various regions.
                </p>

                {/* Why Use the Weight Conversion Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use the Weight Conversion Calculator?
                </h4>
                <p>
                  The Weight Conversion Calculator simplifies the process of
                  converting weight units such as kilograms (kg), pounds (lbs),
                  and ounces (oz). It is useful in a variety of settings,
                  including logistics, nutrition, and retail, to ensure accurate
                  conversions across different systems of measurement.
                </p>

                {/* Benefits of Using the Weight Conversion Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using the Weight Conversion Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Simplifies Measurement Conversions:</strong> Easily
                    convert between different weight units to match
                    international standards.
                  </li>
                  <li>
                    <strong>
                      Useful for Logistics and International Trade:
                    </strong>{" "}
                    Ensures consistency in weight measurements across borders
                    and different countries.
                  </li>
                  <li>
                    <strong>Supports Food and Nutrition Calculations:</strong>{" "}
                    Helps in converting weight units in nutritional labeling and
                    food packaging.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use the Weight Conversion Calculator?
                </h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Retailers:</strong> Helps with product weight
                    conversions for international shipping and sales.
                  </li>
                  <li>
                    <strong>Food Industry:</strong> Essential for converting
                    ingredient weights in food processing and packaging.
                  </li>
                  <li>
                    <strong>Individuals:</strong> Convenient for personal use
                    when converting weight for health, fitness, or travel
                    purposes.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default WeightConversion;
